import { useState, useEffect, useRef } from "react";
import { crmClientsAPI } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import {
  Search,
  Plus,
  User,
  Building2,
  Phone,
  Mail,
  X,
  Loader2,
  Check,
  AlertCircle
} from "lucide-react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

// Validation helpers
const validateCURP = (curp) => {
  if (!curp) return false;
  const cleaned = curp.toUpperCase().trim();
  if (cleaned.length !== 18) return false;
  const pattern = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d$/;
  return pattern.test(cleaned);
};

const validateRFC = (rfc) => {
  if (!rfc) return false;
  const cleaned = rfc.toUpperCase().trim();
  if (cleaned.length !== 12 && cleaned.length !== 13) return false;
  if (cleaned.length === 12) {
    return /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/.test(cleaned);
  }
  return /^[A-Z&Ñ]{4}\d{6}[A-Z0-9]{3}$/.test(cleaned);
};

const validateIDMEX = (idmex) => {
  if (!idmex) return true;
  const cleaned = idmex.replace(/[\s\-]/g, '');
  if (!/^\d+$/.test(cleaned)) return false;
  return cleaned.length >= 9 && cleaned.length <= 13;
};

/**
 * ClientSelector - Selector de cliente CRM con autocomplete y creación rápida
 * 
 * Props:
 * - value: client_id seleccionado
 * - onChange: callback cuando cambia (client_id, clientData)
 * - required: si es obligatorio
 * - disabled: si está deshabilitado
 * - legacyClientName: nombre legacy para mostrar si no hay client_id
 * - legacyClientPhone: teléfono legacy
 * - legacyClientEmail: email legacy
 * - onLegacyChange: callback para cambios legacy (name, phone, email)
 * - showLegacyFields: mostrar campos legacy si no hay CRM
 * - placeholder: texto placeholder
 * - sourceType: "contract" | "quotation" - para migración real
 * - sourceId: ID del contrato/cotización para migración real
 * - isAdmin: si el usuario es admin (para mostrar botón convertir)
 */
export default function ClientSelector({
  value,
  onChange,
  required = false,
  disabled = false,
  legacyClientName = "",
  legacyClientPhone = "",
  legacyClientEmail = "",
  onLegacyChange,
  showLegacyFields = true,
  placeholder = "Buscar cliente...",
  sourceType = null,  // "contract" | "quotation"
  sourceId = null,    // ID for legacy migration
  isAdmin = false     // Show convert button only for admin
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Form state for create modal
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    client_type: "",
    name: "",
    legal_name: "",
    address: "",
    phone: "",
    email: "",
    curp: "",
    idmex: "",
    rfc: "",
    notes: "",
    status: "activo"
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load selected client data if value is provided
  useEffect(() => {
    if (value && !selectedClient) {
      loadClientById(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadClientById = async (clientId) => {
    try {
      const response = await crmClientsAPI.getById(clientId);
      setSelectedClient(response.data);
    } catch (error) {
      console.error("Error loading client:", error);
    }
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await crmClientsAPI.search(term, 8);
      setSearchResults(response.data || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Error searching clients:", error);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSearchTerm("");
    setShowDropdown(false);
    onChange(client.id, client);
  };

  const handleClearSelection = () => {
    setSelectedClient(null);
    onChange(null, null);
  };

  const openCreateModal = () => {
    // Pre-fill with search term or legacy data
    setFormData({
      client_type: "",
      name: "",
      legal_name: searchTerm || legacyClientName || "",
      address: "",
      phone: legacyClientPhone || "",
      email: legacyClientEmail || "",
      curp: "",
      idmex: "",
      rfc: "",
      notes: "",
      status: "activo"
    });
    setFormErrors({});
    setFormStep(1);
    setCreateModalOpen(true);
    setShowDropdown(false);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.legal_name.trim()) {
      errors.legal_name = formData.client_type === "individual" 
        ? "Nombre completo es obligatorio" 
        : "Razón social es obligatoria";
    }
    
    // phone is OPTIONAL - removed validation
    
    if (formData.client_type === "individual") {
      if (!formData.curp.trim()) {
        errors.curp = "CURP es obligatorio";
      } else if (!validateCURP(formData.curp)) {
        errors.curp = "CURP inválido (18 caracteres)";
      }
      
      if (formData.idmex && !validateIDMEX(formData.idmex)) {
        errors.idmex = "IDMEX inválido (9-13 dígitos)";
      }
    }
    
    if (formData.client_type === "company") {
      if (!formData.rfc.trim()) {
        errors.rfc = "RFC es obligatorio";
      } else if (!validateRFC(formData.rfc)) {
        errors.rfc = "RFC inválido (12-13 caracteres)";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateClient = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const payload = {
        client_type: formData.client_type,
        name: formData.name || formData.legal_name,
        legal_name: formData.legal_name,
        address: formData.address || null,
        phone: formData.phone || null,
        email: formData.email ? formData.email.trim().toLowerCase() : null,
        notes: formData.notes || null,
        status: formData.status
      };
      
      if (formData.client_type === "individual") {
        payload.curp = formData.curp.toUpperCase().trim();
        payload.idmex = formData.idmex ? formData.idmex.trim() : null;
      } else {
        payload.rfc = formData.rfc.toUpperCase().trim();
      }
      
      let newClient;
      
      // Use conversion endpoint if this is a legacy migration
      if (sourceType && sourceId && isAdmin) {
        const params = sourceType === "contract" 
          ? { contract_id: sourceId }
          : { quotation_id: sourceId };
        
        const response = await crmClientsAPI.convertFromLegacy({
          params,
          clientData: payload
        });
        newClient = response.data.client;
        toast.success("Cliente creado y vinculado exitosamente");
      } else {
        // Regular creation
        const response = await crmClientsAPI.create(payload);
        newClient = response.data;
        toast.success("Cliente creado exitosamente");
      }
      
      setCreateModalOpen(false);
      
      // Select the newly created client
      setSelectedClient(newClient);
      onChange(newClient.id, newClient);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      
      // Handle duplicate client (409 Conflict) — offer to select existing
      if (status === 409 && detail && typeof detail === "object" && detail.existing_client_id) {
        const useExisting = window.confirm(
          `${detail.message}\n\n¿Desea seleccionar el cliente existente en vez de crear uno nuevo?`
        );
        if (useExisting) {
          try {
            const existingRes = await crmClientsAPI.getById(detail.existing_client_id);
            const existingClient = existingRes.data;
            setCreateModalOpen(false);
            setSelectedClient(existingClient);
            onChange(existingClient.id, existingClient);
            toast.success("Cliente existente seleccionado");
          } catch {
            toast.error("Error al cargar el cliente existente");
          }
        }
      } else {
        const message = typeof detail === "object" ? detail.message : (detail || "Error al crear cliente");
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };

  // Render selected client or search input
  if (selectedClient) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 p-3 bg-slate-50 border rounded-lg">
          {selectedClient.client_type === "individual" ? (
            <User className="w-5 h-5 text-blue-500 flex-shrink-0" />
          ) : (
            <Building2 className="w-5 h-5 text-purple-500 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-800 truncate">
              {selectedClient.name || selectedClient.legal_name}
            </p>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              {selectedClient.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {selectedClient.phone}
                </span>
              )}
              {selectedClient.email && (
                <span className="flex items-center gap-1 truncate">
                  <Mail className="w-3 h-3" />
                  {selectedClient.email}
                </span>
              )}
            </div>
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearSelection}
              className="flex-shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Show legacy fields if no CRM client and legacy data exists
  if (!value && (legacyClientName || legacyClientPhone) && showLegacyFields) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">Cliente Legacy</p>
            <p className="text-sm text-amber-600">{legacyClientName}</p>
          </div>
          {/* Only show convert button for admin with source context */}
          {isAdmin && sourceType && sourceId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openCreateModal}
              className="flex-shrink-0"
              data-testid="convert-to-crm-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Convertir a CRM
            </Button>
          )}
        </div>
        
        {/* Legacy fields for editing */}
        {onLegacyChange && (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Nombre</Label>
              <Input
                value={legacyClientName}
                onChange={(e) => onLegacyChange("name", e.target.value)}
                placeholder="Nombre cliente"
                disabled={disabled}
              />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input
                value={legacyClientPhone}
                onChange={(e) => onLegacyChange("phone", e.target.value)}
                placeholder="Teléfono"
                disabled={disabled}
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                value={legacyClientEmail}
                onChange={(e) => onLegacyChange("email", e.target.value)}
                placeholder="Email"
                disabled={disabled}
              />
            </div>
          </div>
        )}
        
        {renderCreateModal()}
      </div>
    );
  }

  // Render search input with dropdown
  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={inputRef}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => searchTerm.length >= 2 && setShowDropdown(true)}
            placeholder={placeholder}
            className={cn("pl-10", required && !value && "border-amber-300")}
            disabled={disabled}
            data-testid="client-search-input"
          />
          {searching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={openCreateModal}
          disabled={disabled}
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-auto">
          {searchResults.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              {searchTerm.length < 2 ? (
                "Escribe al menos 2 caracteres..."
              ) : searching ? (
                "Buscando..."
              ) : (
                <div className="space-y-2">
                  <p>No se encontraron clientes</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openCreateModal}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Crear nuevo cliente
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              {searchResults.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleSelectClient(client)}
                  className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b last:border-b-0"
                  data-testid={`client-option-${client.id}`}
                >
                  {client.client_type === "individual" ? (
                    <User className="w-5 h-5 text-blue-500 flex-shrink-0" />
                  ) : (
                    <Building2 className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {client.name || client.legal_name}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      {client.phone && <span>{client.phone}</span>}
                      {client.email && <span className="truncate">{client.email}</span>}
                    </div>
                  </div>
                </button>
              ))}
              <button
                type="button"
                onClick={openCreateModal}
                className="w-full p-3 text-left hover:bg-blue-50 flex items-center gap-2 text-blue-600 border-t"
              >
                <Plus className="w-4 h-4" />
                Crear nuevo cliente
              </button>
            </>
          )}
        </div>
      )}

      {renderCreateModal()}
    </div>
  );

  function renderCreateModal() {
    return (
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
          </DialogHeader>

          {formStep === 1 ? (
            <div className="py-6">
              <p className="text-center text-slate-600 mb-6">
                Selecciona el tipo de cliente
              </p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, client_type: "individual" }));
                    setFormStep(2);
                  }}
                  className="p-6 border-2 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center"
                >
                  <User className="w-10 h-10 mx-auto mb-2 text-blue-500" />
                  <p className="font-medium">Persona Física</p>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, client_type: "company" }));
                    setFormStep(2);
                  }}
                  className="p-6 border-2 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-center"
                >
                  <Building2 className="w-10 h-10 mx-auto mb-2 text-purple-500" />
                  <p className="font-medium">Persona Moral</p>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4 max-h-[55vh] overflow-y-auto">
              {/* Type indicator */}
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                {formData.client_type === "individual" ? (
                  <>
                    <User className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">Persona Física</span>
                  </>
                ) : (
                  <>
                    <Building2 className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">Persona Moral</span>
                  </>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-6 text-xs"
                  onClick={() => setFormStep(1)}
                >
                  Cambiar
                </Button>
              </div>

              {/* Legal Name */}
              <div>
                <Label className="text-sm">
                  {formData.client_type === "individual" ? "Nombre Completo *" : "Razón Social *"}
                </Label>
                <Input
                  value={formData.legal_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                  className={formErrors.legal_name ? "border-red-500" : ""}
                />
                {formErrors.legal_name && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.legal_name}</p>
                )}
              </div>

              {/* Address */}
              <div>
                <Label className="text-sm">Domicilio</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>

              {/* Type specific fields */}
              {formData.client_type === "individual" ? (
                <>
                  <div>
                    <Label className="text-sm">CURP *</Label>
                    <Input
                      value={formData.curp}
                      onChange={(e) => setFormData(prev => ({ ...prev, curp: e.target.value.toUpperCase() }))}
                      maxLength={18}
                      className={formErrors.curp ? "border-red-500" : ""}
                    />
                    {formErrors.curp && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.curp}</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">IDMEX (opcional)</Label>
                    <Input
                      value={formData.idmex}
                      onChange={(e) => setFormData(prev => ({ ...prev, idmex: e.target.value.replace(/\D/g, '') }))}
                      maxLength={13}
                      className={formErrors.idmex ? "border-red-500" : ""}
                    />
                    {formErrors.idmex && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.idmex}</p>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <Label className="text-sm">RFC *</Label>
                  <Input
                    value={formData.rfc}
                    onChange={(e) => setFormData(prev => ({ ...prev, rfc: e.target.value.toUpperCase() }))}
                    maxLength={13}
                    className={formErrors.rfc ? "border-red-500" : ""}
                  />
                  {formErrors.rfc && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.rfc}</p>
                  )}
                </div>
              )}

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          )}

          {formStep === 2 && (
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleCreateClient} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Crear Cliente
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    );
  }
}
